/**
 * The nameplate / QR-code marker on an asset photo.
 *
 * Storage is `asset_documents.category`. There is no schema work here — the
 * taxonomy predates this feature; four writers have each spelled it slightly
 * differently, so writes are canonical and reads accept the supersets.
 */

/** What we write. Matches the Firebase sync's `toAssetDocumentCategory`. */
export type AssetPhotoTag = "tag" | "qr_code" | null;

/** `category` for an untagged photo. */
export const UNTAGGED_CATEGORY = "general";

/**
 * Nameplate spellings in prod (`tag` 14.7k, `asset_plate` 12k, `nameplate`).
 * Mirrors `process-asset-ai`'s NAMEPLATE_CATEGORIES and `update-assets`, so a
 * Flutter-written photo displays as tagged even though we'd write `tag`.
 */
const NAMEPLATE_CATEGORIES = ["tag", "asset_plate", "nameplate"];

/** QR spellings in prod (`qr_code` 10.2k, `qr` 396). */
const QR_CATEGORIES = ["qr_code", "qr"];

/**
 * `'tag'` is the only value any frontend branches on
 * (`src/qa/hooks/useAssetReviewPhotos.ts`, and robotic-imaging-web's
 * AssetDetailDialog synthesises exactly this for the nameplate slot), which is
 * why nameplate writes use it rather than `asset_plate`.
 */
export function isNameplateCategory(category: string | null | undefined): boolean {
  return !!category && NAMEPLATE_CATEGORIES.includes(category);
}

export function isQrCodeCategory(category: string | null | undefined): boolean {
  return !!category && QR_CATEGORIES.includes(category);
}

/** The tag a stored `category` represents, collapsing legacy spellings. */
export function tagFromCategory(category: string | null | undefined): AssetPhotoTag {
  if (isNameplateCategory(category)) return "tag";
  if (isQrCodeCategory(category)) return "qr_code";
  return null;
}

/**
 * The `category` to persist for a tag.
 *
 * `category` is a single column, so nameplate and QR are mutually exclusive
 * here — unlike the web forms dialog where a photo carries independent
 * `isAssetPlate` / `isQRCode` flags. That matches how the sync already collapses
 * them (`isAssetPlate` wins).
 */
export function categoryForTag(tag: AssetPhotoTag): string {
  return tag ?? UNTAGGED_CATEGORY;
}

/**
 * The `tags` array to persist alongside `category`.
 *
 * The sync writes `tags: [photo.label]` where label is `general` / `tag` / `qr`,
 * so the array uses the label vocabulary, not the category vocabulary.
 */
export function tagsArrayForTag(tag: AssetPhotoTag): string[] {
  if (tag === "tag") return ["tag"];
  if (tag === "qr_code") return ["qr"];
  return ["general"];
}

/**
 * What clicking a tag button should produce: clicking the tag a photo already
 * carries clears it, clicking the other one switches.
 */
export function toggleTag(
  current: string | null | undefined,
  clicked: Exclude<AssetPhotoTag, null>,
): AssetPhotoTag {
  return tagFromCategory(current) === clicked ? null : clicked;
}
